// Reading a fan's subscription rows: which one belongs to a creator, whether it
// still grants access, and what to say when subscribing fails.

import { format } from 'date-fns';
import { SubscriptionRecord } from '../types/subscription';

/** 'active' and 'cancelled' both grant access; only the panel copy differs. */
export type SubscriptionAccessState = 'none' | 'active' | 'cancelled';

function readString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** Reads one subscription row; null when it carries no usable id. */
export function parseSubscriptionRecord(value: unknown): SubscriptionRecord | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const row = value as Record<string, unknown>;
  if (typeof row.id !== 'string' || row.id === '') {
    return null;
  }

  return {
    id: row.id,
    fanId: readString(row.fanId),
    creatorId: readString(row.creatorId),
    status: readString(row.status),
    priceCredits: typeof row.priceCredits === 'number' ? row.priceCredits : null,
    startedAt: readString(row.startedAt),
    currentPeriodEnd: readString(row.currentPeriodEnd),
    cancelledAt: readString(row.cancelledAt),
  };
}

/** Reads the body of `GET /api/subscriptions/mine`. */
export function parseSubscriptionList(data: unknown): SubscriptionRecord[] {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const rows = (data as Record<string, unknown>).subscriptions;
  if (!Array.isArray(rows)) {
    return [];
  }

  const parsed: SubscriptionRecord[] = [];
  rows.forEach(row => {
    const record = parseSubscriptionRecord(row);
    if (record) {
      parsed.push(record);
    }
  });
  return parsed;
}

/** The fan's row for one creator, or null when they never subscribed. */
export function findSubscription(
  subscriptions: SubscriptionRecord[],
  creatorId: string,
): SubscriptionRecord | null {
  if (!creatorId) {
    return null;
  }
  return subscriptions.find(sub => sub.creatorId === creatorId) ?? null;
}

function toTime(iso: string | null): number | null {
  if (!iso) {
    return null;
  }
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Mirrors `hasActiveAccess` / `isCurrentlyActive` in the backend's
 * subscriptions.util.ts: a cancelled row keeps access until the month the fan
 * already paid for runs out. Anything past its `currentPeriodEnd`, unparsable
 * or missing reads as 'none', which puts the Subscribe CTA back on screen.
 */
export function subscriptionAccessState(
  subscription: SubscriptionRecord | null,
  now: Date,
): SubscriptionAccessState {
  if (!subscription) {
    return 'none';
  }

  if (subscription.status !== 'active' && subscription.status !== 'cancelled') {
    return 'none';
  }

  const end = toTime(subscription.currentPeriodEnd);
  if (end === null || end <= now.getTime()) {
    return 'none';
  }

  return subscription.status === 'active' ? 'active' : 'cancelled';
}

/**
 * The end of the paid period as a date, or null when it is missing or
 * unreadable so the caller can fall back to wording without a date.
 */
export function formatPeriodEnd(iso: string | null): string | null {
  const time = toTime(iso);
  if (time === null) {
    return null;
  }
  return format(new Date(time), 'MMMM d, yyyy');
}

export interface SubscribeFailure {
  message: string;
  /** Show a link to /buy-credits: the only fix is topping up. */
  showBuyCredits: boolean;
  /** The fan already holds this subscription; refresh rather than report. */
  alreadySubscribed: boolean;
}

/**
 * Turns a failed subscribe or cancel into something a fan can act on. Keyed on
 * the backend's own literal messages so a raw axios string or a bare status
 * code never reaches the screen.
 */
export function subscribeFailure(
  status: number | undefined,
  serverMessage: string,
): SubscribeFailure {
  const trimmed = (serverMessage || '').trim();

  if (status === 400 && trimmed === 'Insufficient credits') {
    return {
      message: "You don't have enough credits to subscribe.",
      showBuyCredits: true,
      alreadySubscribed: false,
    };
  }

  if (status === 409) {
    return {
      message: "You're already subscribed to this creator.",
      showBuyCredits: false,
      alreadySubscribed: true,
    };
  }

  if (status === 404 && trimmed === 'Plan not found') {
    return {
      message: 'This creator is not taking subscriptions right now.',
      showBuyCredits: false,
      alreadySubscribed: false,
    };
  }

  return {
    message: trimmed || 'Something went wrong. Please try again.',
    showBuyCredits: false,
    alreadySubscribed: false,
  };
}

/**
 * The backend answers the same `POST /api/subscriptions/:creatorId` with 200
 * when it resumed an existing cancelled row for free and 201 when it created
 * and charged a fresh month. See `subscribe()` in the backend's
 * subscriptions.controller.ts: `resumed` chooses the status code and is *not* a
 * body field, so the status is the only thing the frontend can read.
 */
export function wasResumedWithoutCharge(status: number | undefined): boolean {
  return status === 200;
}

/** The promise the Resume button has to make good on. */
export function resumeNotice(periodEnd: string | null): string {
  return periodEnd
    ? `Resuming is free — you have already paid through ${periodEnd}.`
    : 'Resuming is free — you have already paid for the current period.';
}
